import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Card } from './ui/card';
import { ChevronDown, Trash2, Search, ArrowUpCircle, Info, X, FileClock, KeyRound } from 'lucide-react';
// 修改导入方式
import api, { ENDPOINTS } from '../config/api';
import { toast } from 'react-hot-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Empty, EmptyDescription, EmptyIcon, EmptyTitle } from './ui/empty';
import PageHeader from './PageHeader';
import { motion, AnimatePresence } from 'framer-motion';

const LICENSES_PER_PAGE = 10;

// 空记录提示弹窗组件
const EmptyNoticeModal = ({ isOpen, onClose, serviceName }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="app-modal-layer flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="modal-surface"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 关闭按钮 */}
            <button
              onClick={onClose}
              className="icon-button absolute right-4 top-4 text-muted-foreground"
              aria-label="关闭"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
            
            {/* 内容区域 */}
            <div className="p-8 text-center">
              {/* 图标 */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="mx-auto mb-6 grid size-16 place-items-center rounded-lg border border-accent/20 bg-accent/10"
              >
                <Info className="size-7 text-accent" />
              </motion.div>
              
              {/* 标题 */}
              <h3 className="mb-3 text-2xl font-bold text-foreground">
                暂无许可证记录
              </h3>
              
              {/* 描述 */}
              <p className="mb-6 leading-relaxed text-muted-foreground">
                未找到 <span className="font-semibold text-accent">{serviceName}</span> 的许可证记录
                <br />
                <span className="text-sm">您可以前往许可证生成器创建新的许可证</span>
              </p>
              
              {/* 按钮 */}
              <div className="flex justify-center">
                <Button
                  onClick={onClose}
                  className="px-10"
                >
                  我知道了
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

// 确认对话框组件
const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, configName }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="app-modal-layer flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="modal-surface modal-surface--danger"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="mb-3 text-xl font-bold text-foreground">{title}</h3>
              <p className="mb-6 text-muted-foreground">
                确定要删除许可证记录 
                <span className="text-red-600 dark:text-red-400 font-semibold mx-1">"{configName}"</span> 
                吗？此操作将从数据库中永久删除该记录，不可恢复。
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={onClose} 
                  className="flex-1 border-2"
                >
                  取消
                </Button>
                <Button variant="destructive" onClick={onConfirm} className="flex-1">
                  删除
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const LicenseHistory = () => {
  const { service } = useParams();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    licenseId: null,
    configName: ''
  });

  // 新增搜索和分页状态
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredLicenses, setFilteredLicenses] = useState([]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = React.useRef(null);
  const hasShownEmptyToast = React.useRef(false); // 添加标记，防止重复显示提示
  const [showEmptyModal, setShowEmptyModal] = useState(false); // 空记录弹窗状态

  // 监听滚动事件 - 改为监听容器的滚动
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setShowScrollTop(scrollContainer.scrollTop > 300);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // 返回顶部函数 - 改为滚动容器
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // 获取服务目录名称
  const getServiceDir = (serviceId) => {
    if (!serviceId) return '';
    
    const serviceDirs = {
      'conf': 'confluence',
      'confluence': 'confluence',
      'jc': 'jira_core',
      'jira-core': 'jira_core',
      'jira': 'jira_software',
      'jira-software': 'jira_software',
      'jsm': 'jira_service_management',
      'crowd': 'crowd',
      'crucible': 'crucible',
      'bitbucket': 'bitbucket',
      'bamboo': 'bamboo',
      'plugin': 'plugins',
      'plugins': 'plugins'
    };
    return serviceDirs[serviceId] || serviceId;
  };

  // 获取显示名称
  const getServiceName = (serviceId) => {
    const serviceNames = {
      'confluence': 'Confluence',
      'conf': 'Confluence',
      'jira-core': 'JIRA Core',
      'jc': 'JIRA Core',
      'jira-software': 'JIRA Software',
      'jira': 'JIRA Software',
      'jsm': 'JIRA Service Management',
      'crowd': 'Crowd',
      'crucible': 'Crucible',
      'bitbucket': 'Bitbucket',
      'bamboo': 'Bamboo',
      'plugins': '第三方插件',
      'plugin': '第三方插件'
    };
    return serviceNames[serviceId] || serviceId;
  };

  // 获取许可证列表
  const fetchLicenses = async () => {
    try {
      if (!service) {
        setLicenses([]);
        setLoading(false);
        return;
      }

      // 直接使用原始的 service 参数，不进行映射
      // 因为后端保存时使用的是原始的服务名称
      const response = await api.get(ENDPOINTS.GET_LICENSES(service));
      const data = response.data;
      
      // 处理各种数据格式：数组、null、undefined
      if (data === null || data === undefined || (Array.isArray(data) && data.length === 0)) {
        // 没有数据，设置为空数组并显示弹窗
        setLicenses([]);
        
        // 使用 ref 标记，防止重复显示提示（React.StrictMode 会导致 useEffect 执行两次）
        if (!hasShownEmptyToast.current) {
          hasShownEmptyToast.current = true;
          // 延迟显示弹窗，避免与页面加载冲突
          setTimeout(() => {
            setShowEmptyModal(true);
          }, 300);
          // 3秒后重置标记，允许再次显示（如果用户切换到其他服务）
          setTimeout(() => {
            hasShownEmptyToast.current = false;
          }, 3000);
        }
      } else if (Array.isArray(data)) {
        // 处理每个许可证对象，确保所有必要的字段都存在
        const processedData = data.map(license => ({
          id: license.id || '',
          fileName: license.fileName || '未知文件',
          configName: license.configName || '未知配置',
          createdAt: license.createdAt || new Date().toISOString(),
          content: license.content || '',
          '授权名': license['授权名'] || '未知',
          '邮箱': license['邮箱'] || '',
          '访问域名': license['访问域名'] || '',
          '服务器ID': license['服务器ID'] || '',
          '许可证类型': license['许可证类型'] || '',
          '插件密钥': license['插件密钥'] || '',
          '有效期': license['有效期'] || '默认有效期' // 添加有效期字段
        }));
        setLicenses(processedData);
      } else {
        // console.error('Invalid data format received:', data);
        setLicenses([]);
        throw new Error('服务器返回的数据格式不正确');
      }
      
      setError(null);
    } catch (error) {
      // console.error('Error fetching licenses:', error);
      setError(error.message || '获取许可证历史记录失败');
      toast.error('获取许可证历史记录失败: ' + (error.message || '未知错误'));
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 当 service 改变时，重置标记
    hasShownEmptyToast.current = false;
    fetchLicenses();
  }, [service]);

  // 搜索和过滤逻辑
  useEffect(() => {
    if (!licenses) return;

    const filtered = licenses.filter(license => {
      const searchStr = searchQuery.toLowerCase();
      return (
        license.configName?.toLowerCase().includes(searchStr) ||
        license['授权名']?.toLowerCase().includes(searchStr) ||
        license['邮箱']?.toLowerCase().includes(searchStr) ||
        license['访问域名']?.toLowerCase().includes(searchStr) ||
        license['服务器ID']?.toLowerCase().includes(searchStr)
      );
    });

    setFilteredLicenses(filtered);
    setCurrentPage(1); // 重置到第一页
  }, [licenses, searchQuery]);

  // 计算分页数据
  const totalPages = Math.ceil(filteredLicenses.length / LICENSES_PER_PAGE);
  const startIndex = (currentPage - 1) * LICENSES_PER_PAGE;
  const endIndex = startIndex + LICENSES_PER_PAGE;
  const currentLicenses = filteredLicenses.slice(startIndex, endIndex);

  // 分页控制
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // 处理删除许可证
  const handleDelete = async (licenseId, configName) => {
    if (!licenseId) {
      toast.error('无效的许可证ID');
      return;
    }
    
    setConfirmDialog({
      isOpen: true,
      licenseId,
      configName: configName || '未知配置'
    });
  };

  // 确认删除操作
  const confirmDelete = async () => {
    try {
      const { licenseId } = confirmDialog;
      if (!licenseId || !service) {
        throw new Error('缺少必要的参数');
      }

      // 直接使用原始的 service 参数，不进行映射
      await api.delete(ENDPOINTS.DELETE_LICENSE(service, licenseId));
      
      toast.success('许可证已删除');
      await fetchLicenses(); // 重新加载列表
    } catch (error) {
      // console.error('Delete error:', error);
      toast.error('删除许可证失败: ' + (error.response?.data?.error || error.message || '未知错误'));
    } finally {
      setConfirmDialog({ isOpen: false, licenseId: null, configName: '' });
    }
  };

  // 获取许可证内容
  const getLicenseContent = (content) => {
    try {
      if (!content || typeof content !== 'string') return '无许可证内容';
      const parts = content.split('=== Generated License ===');
      return parts.length > 1 ? parts[1].trim() : content.trim();
    } catch (error) {
      // console.error('Error processing license content:', error);
      return '处理许可证内容时出错';
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      // 检查是否是有效日期
      if (isNaN(date.getTime())) {
        return '无效日期';
      }
      
      // 使用本地时区格式化，避免时区转换问题
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      // console.error('Error formatting date:', error);
      return '无效日期';
    }
  };

  if (loading) {
    return (
      <div className="history-page history-page--state">
        <div className="history-state" role="status" aria-live="polite">
          <div className="system-spinner" aria-hidden="true"></div>
          <strong>正在载入许可证档案</strong>
          <span>正在整理 {getServiceName(service)} 的历史记录…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page history-page--state">
        <div className="history-state history-state--error" role="alert">
          <span className="history-state__icon"><Info /></span>
          <strong>许可证档案载入失败</strong>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <PageHeader
        className="history-page-header"
        eyebrow="许可证档案"
        title={`${getServiceName(service)} 许可证记录`}
        description="集中检索、查看并维护已生成的许可证数据。"
        icon={FileClock}
        badge={`${filteredLicenses.length} 条记录`}
      />

      <Card className="history-search-card">
          <div className="history-toolbar__copy">
            <span className="history-toolbar__icon" aria-hidden="true"><Search /></span>
            <div>
              <h2>查找许可证档案</h2>
              <p>可按配置名称、授权名、邮箱、域名或服务器 ID 检索。</p>
            </div>
          </div>
          <div className="history-search" role="search">
            <Search className="history-search__icon" aria-hidden="true" />
            <Input
              type="search"
              aria-label="搜索许可证记录"
              placeholder="搜索许可证记录"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="history-search__input"
            />
            {searchQuery && (
              <button
                type="button"
                className="history-search__clear"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setSearchQuery('')}
                aria-label="清除搜索内容"
              >
                <X />
              </button>
            )}
          </div>
      </Card>

      <section className="history-results" aria-label="许可证记录列表">
        <div className="history-results__header">
          <div>
            <h2>{searchQuery ? '搜索结果' : '全部许可证'}</h2>
          </div>
          <span className="history-results__count">
            {searchQuery ? `${filteredLicenses.length} / ${licenses.length}` : filteredLicenses.length} 条
          </span>
        </div>

      <div ref={scrollContainerRef} className="history-list custom-scrollbar">
        {currentLicenses.length === 0 ? (
          <Empty className="history-empty">
            <EmptyIcon className="history-empty__icon"><Search /></EmptyIcon>
            <EmptyTitle>{searchQuery ? '没有找到匹配记录' : '暂无许可证记录'}</EmptyTitle>
            <EmptyDescription>
              {searchQuery
                ? `没有与“${searchQuery}”匹配的档案，请尝试其他关键词。`
                : `当前还没有 ${getServiceName(service)} 许可证档案，可前往许可证生成器创建。`}
            </EmptyDescription>
            {searchQuery && (
              <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                清除搜索
              </Button>
            )}
          </Empty>
        ) : currentLicenses.map((license, index) => {
            const licenseContent = getLicenseContent(license.content);
            const licenseLineCount = licenseContent
              ? licenseContent.split(/\r?\n/).filter(line => line.trim()).length
              : 0;

            return (
              <Card
                key={license.id || index}
                className={`license-record${expandedId === license.id ? ' is-expanded' : ''}`}
              >
                <div className="license-record__inner">
                  <button
                    type="button"
                    className="license-record__summary"
                    onClick={() => setExpandedId(expandedId === license.id ? null : license.id)}
                    aria-expanded={expandedId === license.id}
                  >
                    <span className="license-record__index" aria-hidden="true">
                        {startIndex + index + 1}
                    </span>
                    <span className="license-record__identity">
                        <strong>
                          {license.configName || '未知配置'}
                        </strong>
                        <span>
                          <i aria-hidden="true"></i>
                            {license['授权名'] || '未知'}
                        </span>
                    </span>
                    <span className="license-record__meta">
                      <time dateTime={license.createdAt}>创建于 {formatDate(license.createdAt)}</time>
                      <span className="license-record__type">
                        {license['许可证类型'] || '标准版'}
                      </span>
                    </span>
                    <span className={`license-record__chevron${expandedId === license.id ? ' is-open' : ''}`} aria-hidden="true">
                      <ChevronDown />
                    </span>
                  </button>

                  {expandedId === license.id && (
                    <div className="license-record__details" onClick={(e) => e.stopPropagation()}>
                      <div className="license-detail__intro">
                        <div className="license-detail__intro-title">
                          <span aria-hidden="true"><FileClock /></span>
                          <div>
                            <small>ARCHIVED LICENSE</small>
                            <strong>许可证档案详情</strong>
                            <p>核对归档参数及生成时保存的许可证原始内容。</p>
                          </div>
                        </div>
                        <div className="license-detail__intro-meta">
                          <span>{getServiceName(service)}</span>
                          <time dateTime={license.createdAt}>{formatDate(license.createdAt)}</time>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="license-detail__delete"
                            onClick={() => handleDelete(license.id, license.configName)}
                          >
                            <Trash2 data-icon="inline-start" />
                            <span>删除记录</span>
                          </Button>
                        </div>
                      </div>

                      <div className="license-detail__grid">
                        <div className="license-detail__section">
                          <div className="license-detail__section-heading">
                            <span aria-hidden="true"><Info /></span>
                            <div>
                              <small>IDENTITY</small>
                              <h4>基本信息</h4>
                            </div>
                          </div>
                          <dl className="license-detail__items">
                            <div className="license-detail__item" data-tone="blue">
                              <i aria-hidden="true"></i>
                              <div><dt>邮箱地址</dt><dd>{license['邮箱'] || '未设置'}</dd></div>
                            </div>
                            <div className="license-detail__item" data-tone="blue">
                              <i aria-hidden="true"></i>
                              <div><dt>访问域名</dt><dd>{license['访问域名'] || '未设置'}</dd></div>
                            </div>
                            <div className="license-detail__item" data-tone="purple">
                              <i aria-hidden="true"></i>
                              <div><dt>服务器 ID</dt><dd className="is-mono">{license['服务器ID'] || '未设置'}</dd></div>
                            </div>
                          </dl>
                        </div>

                        <div className="license-detail__section">
                          <div className="license-detail__section-heading">
                            <span aria-hidden="true"><KeyRound /></span>
                            <div>
                              <small>LICENSE PROFILE</small>
                              <h4>许可证详情</h4>
                            </div>
                          </div>
                          <dl className="license-detail__items">
                            <div className="license-detail__item" data-tone="indigo">
                              <i aria-hidden="true"></i>
                              <div><dt>许可证类型</dt><dd>{license['许可证类型'] || '未设置'}</dd></div>
                            </div>
                            <div className="license-detail__item" data-tone="orange">
                              <i aria-hidden="true"></i>
                              <div><dt>有效期</dt><dd>{license['有效期'] || '默认有效期'}</dd></div>
                            </div>
                            <div className="license-detail__item" data-tone="blue">
                              <i aria-hidden="true"></i>
                              <div><dt>归档时间</dt><dd>{formatDate(license.createdAt)}</dd></div>
                            </div>

                            {license['插件密钥'] && (
                              <div className="license-detail__item" data-tone="cyan">
                                <i aria-hidden="true"></i>
                                <div><dt>插件密钥</dt><dd className="is-mono">{license['插件密钥']}</dd></div>
                              </div>
                            )}
                          </dl>
                        </div>
                      </div>

                      <div className="license-content">
                        <div className="license-content__heading">
                          <div className="license-content__title">
                            <span aria-hidden="true"><KeyRound /></span>
                            <div>
                              <strong>许可证原始内容</strong>
                              <small>LICENSE PAYLOAD</small>
                            </div>
                          </div>
                          <div className="license-content__meta" aria-label="许可证内容统计">
                            <span>{licenseLineCount} 行</span>
                            <span>{licenseContent.length.toLocaleString('zh-CN')} 字符</span>
                            <span>只读</span>
                          </div>
                        </div>
                        <pre tabIndex="0"><code>{licenseContent}</code></pre>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
      </div>

      {currentLicenses.length > 0 && (
        <div className="history-pagination">
            <span className="history-pagination__summary">
              第 {startIndex + 1}–{Math.min(endIndex, filteredLicenses.length)} 条
              <span aria-hidden="true">·</span>
              共 {filteredLicenses.length} 条
              <span aria-hidden="true">·</span>
              每页 {LICENSES_PER_PAGE} 条
            </span>
            <div className="history-pagination__controls">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              上一页
            </Button>
            
            {totalPages <= 7 ? (
              // 当页数较少时，全部显示
              [...Array(totalPages)].map((_, index) => (
                <Button
                  key={index + 1}
                  variant={currentPage === index + 1 ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(index + 1)}
                  className="history-pagination__page"
                >
                  {index + 1}
                </Button>
              ))
            ) : (
              // 当页数较多时，使用省略显示
              <>
                <Button
                  variant={currentPage === 1 ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  className="history-pagination__page"
                >
                  1
                </Button>
                
                {currentPage > 3 && <span className="px-1">...</span>}
                
                {[...Array(5)].map((_, i) => {
                  const pageNum = Math.max(2, currentPage - 2) + i;
                  if (pageNum > 1 && pageNum < totalPages) {
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="history-pagination__page"
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                }).filter(Boolean)}
                
                {currentPage < totalPages - 2 && <span className="px-1">...</span>}
                
                <Button
                  variant={currentPage === totalPages ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  className="history-pagination__page"
                >
                  {totalPages}
                </Button>
              </>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              下一页
            </Button>
            </div>
        </div>
      )}
      </section>

      {/* 返回顶部按钮 - 固定在右下角 */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="floating-action fixed bottom-20 right-4 z-50 sm:bottom-8 sm:right-8"
          aria-label="返回顶部"
        >
          <ArrowUpCircle className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>
      )}

      {/* 空记录提示弹窗 */}
      <EmptyNoticeModal
        isOpen={showEmptyModal}
        onClose={() => setShowEmptyModal(false)}
        serviceName={getServiceName(service)}
      />

      {/* 确认删除对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, licenseId: null, configName: '' })}
        onConfirm={confirmDelete}
        title="确认删除"
        configName={confirmDialog.configName}
      />
    </div>
  );
};

export default LicenseHistory;
